import { List, Map, Set } from 'immutable';

import { shapeScheduledUpdatesForApp } from '@/utils/schedule/updates';
import { getTimeRangeEnd, getToday } from '@/utils/schedule/time';
import { shapeId, shapeNumber } from '@/utils/listings/listings';
import { getDateKey, isAfter } from '@/utils/time';
import { createController } from '@/utils/controller';
import { getPagination } from '@/utils/schedule/pagination';
import { getSize } from '@/utils/iterable/getSize';
import { api } from '@/utils/api';

import { MESSAGE, NOTIFICATION } from '@/constants/notifications';
import { EMPTY } from '@/constants';

import actions from '@/actions';

const controller = createController();

function* bootstrap(state) {
  controller.stop();

  const channel = state.getIn(['schedule', 'channel']);
  const shopId = state.getIn(['schedule', 'shopId']);
  const db = state.getIn(['schedule', 'db']);

  if (!channel || !db || !shopId) {
    return state.deleteIn(['schedule', 'updates']);
  }

  const updates = Map({
    channel,
    db,
    pagination: Map(getPagination()),
    shopId,
    updates: EMPTY.MAP,
  });

  yield function effect(dispatch) {
    dispatch(actions.schedule.updates.getUpdates({ clear: true }));
  };

  return state.setIn(['schedule', 'updates'], updates);
}

function* cleanUp(state) {
  controller.stop();
  return state.deleteIn(['schedule', 'updates']);
}

function* deleteUpdates(state, ids) {
  const updateIds = Set(ids);

  yield function effect(dispatch) {
    const db = state.getIn(['schedule', 'updates', 'db']);
    const shopId = state.getIn(['schedule', 'updates', 'shopId']);

    function mapIds(updateId) {
      return api.schedule.updates.delete({ db, shopId, updateId });
    }

    Promise
      .all(ids.map(mapIds))
      .then(
        () => dispatch(actions.schedule.updates.deleteUpdates.fulfilled({ shopId, updateIds })),
        (error) => dispatch(actions.schedule.updates.deleteUpdates.rejected({ error, shopId, updateIds })),
      );
  };

  function updateProcessing(processing = EMPTY.SET) {
    return processing.union(updateIds);
  }

  return state.updateIn(['schedule', 'updates', 'updates', 'processing'], updateProcessing);
}

function* deleteUpdatesFulfilled(state, { shopId, updateIds }) {
  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: MESSAGE.SUCCESS.DELETE_UPDATES,
      })
    );

    if (state.getIn(['schedule', 'shopId']) === shopId) {
      dispatch(actions.schedule.getCounts());
    }

    if (shopId === state.getIn(['schedule', 'updates', 'shopId'])) {
      dispatch(actions.schedule.updates.getUpdates({ clear: true }));
    }

    if (updateIds.has(shapeId(state.getIn(['shops', 'schedule', shopId, 'nextPublish', 'id'])))) {
      dispatch(actions.shops.getScheduled(shopId));
    }
  };

  return state;
}

function* deleteUpdatesRejected(state, { error, shopId, updateIds }) {
  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DELETE_UPDATES,
      })
    );

    if (state.getIn(['schedule', 'shopId']) === shopId) {
      dispatch(actions.schedule.getCounts());
    }

    if (shopId === state.getIn(['schedule', 'updates', 'shopId'])) {
      dispatch(actions.schedule.updates.getUpdates({ clear: true }));
    }

    if (updateIds.has(shapeId(state.getIn(['shops', 'schedule', shopId, 'nextPublish', 'id'])))) {
      dispatch(actions.shops.getScheduled(shopId));
    }
  };

  return state;
}

function* getUpdates(state, { clear, more } = {}) {
  const { signal } = controller.start();
  const updates = state.getIn(['schedule', 'updates']);
  const shopId = updates.get('shopId');
  const db = updates.get('db');

  if (!shopId) {
    return state;
  }

  yield function effect(dispatch) {
    const pagination = updates.get('pagination');
    const limit = pagination.get('limit');
    const offset = clear
      ? 0
      : more
        ? pagination.get('offset') + limit
        : pagination.get('offset');

    api.schedule.listings
      .get({ db, params: { limit, offset, type: 'updates' }, shopId, signal })
      .then(
        (response) => dispatch(actions.schedule.updates.getUpdates.fulfilled({ limit, offset, response, shopId })),
        (error) => dispatch(actions.schedule.updates.getUpdates.rejected({ error, shopId, signal })),
      );
  };

  return state.getIn(['schedule', 'updates', 'loading'])
    ? state
    : state.setIn(['schedule', 'updates', 'loading'], true);
}

function* getUpdatesFulfilled(state, { limit, offset, shopId, response }) {
  if (shopId !== state.getIn(['schedule', 'updates', 'shopId'])) return state;

  controller.stop();

  let total = shapeNumber(response.total);
  let updates = state.getIn(['schedule', 'updates']).delete('loading');

  if (offset === 0 || total === 0) {
    updates = updates
      .set('dates', EMPTY.MAP)
      .set('ranges', EMPTY.MAP)
      .set('updates', Map({ total }))
      .setIn(['pagination', 'offset'], 0);

    if (total === 0) {
      const today = getToday();
      const key = getDateKey(today);

      updates = updates
        .setIn(['updates', 'loaded'], 0)
        .set('ranges', Map({ keys: List([key]), timestamps: Map({ [key]: today }) }))
        .set('time', Map( { today, start: today, end: getTimeRangeEnd(today) }));

      return state.setIn(['schedule', 'updates'], updates);
    }
  } else if (getSize(response.scheduled_updates) === 0 && offset > 0) {
    total = updates.getIn(['updates', 'loaded'], 0);

    updates = updates
      .setIn(['pagination', 'offset'], Math.floor(Math.max(0, total - 1) / limit) * limit)
      .setIn(['updates', 'total'], total);

    return state.setIn(['schedule', 'updates'], updates);
  } else {
    if (offset !== updates.getIn(['pagination', 'offset'])) {
      updates = updates.setIn(['pagination', 'offset'], offset);
    }

    if (total !== updates.getIn(['updates', 'total'])) {
      updates = updates.setIn(['updates', 'total'], total);
    }
  }

  updates = shapeScheduledUpdatesForApp({ response, state: updates });

  if (total > 0 && updates.getIn(['updates', 'loaded']) === updates.getIn(['updates', 'total'])) {
    const lastRange = updates.getIn(['ranges', 'timestamps', updates.getIn(['ranges', 'keys']).last()]);

    if (isAfter(updates.getIn(['time', 'start']), lastRange)) {
      updates = updates
        .setIn(['time', 'start'], lastRange)
        .setIn(['time', 'end'], getTimeRangeEnd(lastRange));
    }
  }

  return state.setIn(['schedule', 'updates'], updates);
}

function* getUpdatesRejected(state, { error, shopId, signal }) {
  if (signal.aborted || shopId !== state.getIn(['schedule', 'updates', 'shopId'])) return state;

  controller.stop();

  yield function effect(dispatch) {
    console.error(error);

    if (state.getIn(['schedule', 'updates', 'shopId']) === shopId) {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.ERROR,
          message: MESSAGE.FAIL.LOAD_UPDATES,
        })
      );
    }
  };

  return state.deleteIn(['schedule', 'updates', 'loading']);
}

function* setTimeRange(state, { start }) {
  return state
    .setIn(['schedule', 'updates', 'time', 'start'], start)
    .setIn(['schedule', 'updates', 'time', 'end'], getTimeRangeEnd(start));
}

function* toggleUpdate(state, { updateId }) {
  function updateSelected(selected = EMPTY.ORDERED_SET) {
    return selected.has(updateId)
      ? selected.delete(updateId)
      : selected.add(updateId);
  }

  return state.updateIn(['schedule', 'updates', 'updates', 'selected'], updateSelected);
}

function* unschedule(state, { index, productId, updateId }) {
  if (state.getIn(['schedule', 'updates', 'updates', 'byId', updateId, 'products', index, 'id']) !== productId) {
    return state;
  }

  yield function effect(dispatch) {
    const updates = state.getIn(['schedule', 'updates']);
    const shopId = updates.get('shopId');

    api.schedule.listings.delete({ productId,updateId })
      .then(
        () => dispatch(actions.schedule.updates.unschedule.fulfilled({ shopId, updateId })),
        (error) => {
          dispatch(actions.schedule.updates.unschedule.rejected({ error, index, productId, shopId, updateId }));
        },
      );
  };

  return state.setIn(['schedule', 'updates', 'updates', 'byId', updateId, 'products', index, 'processing'], true);
}

function* unscheduleFulfilled(state, { shopId, updateId }) {
  if (shopId === state.getIn(['schedule', 'updates', 'shopId'])) {
    yield function effect(dispatch) {
      dispatch(
        actions.notifications.add({
          type: NOTIFICATION.SUCCESS,
          message: MESSAGE.SUCCESS.DISCONNECT_LISTINGS,
        })
      );

      if (shapeId(state.getIn(['shops', 'schedule', shopId, 'nextPublish', 'id'])) === updateId) {
        dispatch(actions.shops.getScheduled(shopId));
      }

      if (state.getIn(['schedule', 'shopId']) === shopId) {
        dispatch(actions.schedule.getCounts());
      }

      if (state.getIn(['schedule', 'updates', 'shopId']) === shopId) {
        dispatch(actions.schedule.updates.getUpdates({ clear: true }));
      }
    };
  }

  return state;
}

function* unscheduleRejected(state, { error, index, productId, shopId, updateId }) {
  yield function effect(dispatch) {
    if (error) console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.DISCONNECT_LISTINGS,
      })
    );
  };

  function updateProduct(product) {
    return product.delete('processing');
  }

  return (
    state.getIn(['schedule', 'updates', 'shopId']) === shopId &&
    state.getIn(['schedule', 'updates', 'updates', 'byId', updateId, 'products', index, 'id']) === productId
  )
    ? state.updateIn(['schedule', 'updates', 'updates', 'byId', updateId, 'products', index], updateProduct)
    : state;
}

const scheduleUpdates = {
  [actions.schedule.updates.bootstrap]: bootstrap,
  [actions.schedule.updates.cleanUp]: cleanUp,
  [actions.schedule.updates.deleteUpdates]: deleteUpdates,
  [actions.schedule.updates.deleteUpdates.fulfilled]: deleteUpdatesFulfilled,
  [actions.schedule.updates.deleteUpdates.rejected]: deleteUpdatesRejected,
  [actions.schedule.updates.getUpdates]: getUpdates,
  [actions.schedule.updates.getUpdates.fulfilled]: getUpdatesFulfilled,
  [actions.schedule.updates.getUpdates.rejected]: getUpdatesRejected,
  [actions.schedule.updates.setTimeRange]: setTimeRange,
  [actions.schedule.updates.toggleUpdate]: toggleUpdate,
  [actions.schedule.updates.unschedule]: unschedule,
  [actions.schedule.updates.unschedule.fulfilled]: unscheduleFulfilled,
  [actions.schedule.updates.unschedule.rejected]: unscheduleRejected,
};

export default scheduleUpdates;
